import React, { useEffect, useState, memo } from 'react';
import { XIcon, ChevronDownIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
type SummaryPopupProps = {
  name: string;
  topics: string[];
  mood: string;
  storyText?: string;
  artStyle?: string;
  selectedAge?: string | null;
  selectedStoryType?: 'classic' | 'silly' | 'adventure' | null;
  onClose: () => void;
  onCreate: () => void;
};
type APIResponse = {
  book_id?: string;
  error?: string;
};
export const SummaryPopup = ({
  name = '',
  topics = [],
  mood = '',
  storyText = '',
  artStyle,
  selectedAge = null,
  selectedStoryType = null,
  onClose,
  onCreate
}: SummaryPopupProps) => {
  const navigate = useNavigate();
  const [showRawPrompt, setShowRawPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState<string | null>(null);
  const [editedImagePrompt, setEditedImagePrompt] = useState<string | null>(null);
  const formatTopics = (topics: string[]) => {
    const lowercaseTopics = topics.map(topic => topic.toLowerCase());
    if (lowercaseTopics.length === 1) return lowercaseTopics[0];
    if (lowercaseTopics.length === 2) return `${lowercaseTopics[0]} and ${lowercaseTopics[1]}`;
    const lastTopic = lowercaseTopics[lowercaseTopics.length - 1];
    const otherTopics = lowercaseTopics.slice(0, -1).join(', ');
    return `${otherTopics}, and ${lastTopic}`;
  };
  const transformArtStyle = (style: string | undefined) => {
    switch (style) {
      case 'Crayon':
        return 'Draw in a crayon-style with flat colors like a toddler board book. Keep it low-res and simple.';
      case 'Fuzzy':
        return 'Illustrate in a soft, airbrushed style inspired by Japanese picture books and early San-X designs. The character should feel plush, round, and gentle, like a stuffed animal brought to life. Pastel colors, blurred edges, and a dreamy background — think cozy and magical, but original and not directly copying any known brand';
      case 'Bright':
        return "Create a colorful, high-contrast scene using bold outlines and solid, vibrant colors in a playful, modern picture-book style. The character should be simple and highly readable for young children, with friendly shapes and a warm expression — but not resemble any specific existing character. Think of it as inspired by vintage European children's books with a fresh, original twist. Add a bit of charm or texture to avoid flatness, and give the scene a fun, energetic feel that stands on its own";
      case 'Pretty':
        return "Create a whimsical, sweet, and expressive illustration. The style should feel playful and cartoon-like, with soft, rounded shapes and gentle facial expressions. Keep the energy lively, but avoid sharp angles or intense features—aim for warmth and approachability. Use a bright yet soothing color palette to create a cheerful, inviting mood. The illustration should be full of visual interest, with friendly, curious details that delight young children. Character designs should be endearing and memorable, with soft lines and expressive gestures that clearly convey personality and emotion without feeling overwhelming. The overall tone should be cozy, joyful, and magical—perfect for heartwarming, funny, or adventurous children's stories.";
      default:
        return style;
    }
  };
  const getAgeParameters = (age: string | null) => {
    switch (age) {
      case 'Toddler\n(Ages 2-3)':
        return 'This story is for a toddler (around age 2). Keep the language extremely simple and repetitive, like a board book. Use 1–2 very short sentences per page. Focus on familiar experiences, gentle feelings, and playful or cozy moments. Include rhythm, sound words, and clear, predictable patterns. The tone should be warm, joyful, and soothing — like "Goodnight Moon" or "Brown Bear, Brown Bear." Avoid complex sentences, big words, or detailed plots.\n\n';
      case 'Little Kid\n(Ages 4-5)':
        return 'This story is for a little kid (around ages 4–5). Use short, clear sentences and a friendly, playful tone. Include light story structure with a beginning, middle, and end. The story can be imaginative or silly, but should stay easy to follow. Include curiosity, small challenges, and emotional moments — like making a new friend, helping someone, or learning something small.\n\nUse about 2–3 short sentences per page. Rhyme, repetition, or gentle humor is welcome. The tone should feel like "The Snowy Day" or "Elephant & Piggie" — warm, expressive, and fun to read aloud.\n\n';
      case 'Big Kid\n(Ages 6+)':
        return 'This story is for a big kid (around age 6). Use clear but slightly more advanced language, with 3–5 sentences per page. Include a simple plot with a problem and a resolution. The main character should grow or learn something — like being brave, trying again, or making a friend.\n\nAdd playful or emotional moments, light suspense, or imaginative twists. Dialogue and internal thoughts are welcome. The tone should feel like "Frog and Toad" or "Katie and the Magical Deer" — thoughtful, warm, and satisfying.\n\nAvoid anything too scary, sad, or overly complex. Keep the story fun and easy to follow.\n\n';
      default:
        return '';
    }
  };
  const generatePrompt = () => {
    let prompt = '';
    // 1. Age
    if (selectedAge) {
      const ageParams = getAgeParameters(selectedAge);
      if (ageParams) {
        prompt += `1. Age:\n${ageParams}`;
      } else {
        const age = selectedAge === '6+' ? '7' : selectedAge.split('-')[0] || selectedAge;
        prompt += `1. Age:\nWrite this story for a child who is ${age} years old.\n\n`;
      }
    }
    // 2. Main character name and information
    prompt += `2. Main Character:\nMake a story about ${name || 'a character'}`;
    if (topics.length > 0) prompt += ` with ${formatTopics(topics)}`;
    prompt += '\n\n';
    // 3. Setting
    if (mood) {
      prompt += `3. Setting:\nThe story takes place in ${mood}\n\n`;
    }
    // 4. Story Tone
    if (selectedStoryType) {
      prompt += '4. Story Tone:\n';
      switch (selectedStoryType) {
        case 'classic':
          prompt += `Choose one of these classic adventure arcs:
- "The Quest" – The character sets out to find or fix something important
- "Into the Unknown" – The character is unexpectedly swept into a strange or magical place
- "Bravery Test" – The character faces something that seems scary or hard
- "A New World" – The character visits a place where the rules are different
- "Team Up & Save the Day" – The character joins with others to solve a big problem
Use simple, energetic language with a clear beginning, middle, and end.
End with a cheerful or proud moment showing character growth.\n\n`;
          break;
        case 'silly':
          prompt += `Choose one of these silly story arcs:
- "Normal… Until It Wasn't" – A regular day takes a ridiculous turn
- "Oops! That Wasn't Supposed to Happen" – A small mistake leads to chaos
- "The Talking Thing" – An ordinary object starts talking
- "What If It Had Legs?" – Something that shouldn't move runs away
- "Rule Reversal" – The main character swaps roles with someone else
Keep it light and weird with playful logic and funny dialogue.
End with a surprising or funny conclusion that makes kids laugh.\n\n`;
          break;
        case 'adventure':
          prompt += `Make this an exciting adventure story full of action and discovery.\n\n`;
          break;
      }
    }
    // 5. Additional Story Details
    if (storyText) {
      prompt += `5. Additional Story Details:\n${storyText}\n`;
    }
    return prompt;
  };
  const saveStory = async (): Promise<string | null> => {
    try {
      const summaryText = editedPrompt || generatePrompt();
      const imagePrompt = editedImagePrompt || transformArtStyle(artStyle);
      const url = new URL('https://storybunny.messagesimproved.com/generate_storybook');
      url.searchParams.append('prompt', summaryText);
      if (imagePrompt) url.searchParams.append('art_style', imagePrompt);
      const headers: Record<string, string> = {};
      const accessToken = localStorage.getItem('access_token');
      const userEmail = localStorage.getItem('user_email');
      if (accessToken && userEmail) {
        headers['Authorization'] = accessToken;
        headers['User-Email'] = userEmail;
      }
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Details: ${errorText}`);
      }
      const data = await response.json();
      if (!data.book_id) throw new Error('No book ID received from API');
      const existingStories = JSON.parse(localStorage.getItem('storyBookIds') || '[]');
      const newStory = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        apiResponse: {
          book_id: data.book_id
        },
        summary: {
          name,
          topics,
          mood,
          storyText,
          artStyle
        },
        analysis: {
          vocabulary: Math.floor(Math.random() * 30) + 70,
          creativity: Math.floor(Math.random() * 30) + 70,
          engagement: Math.floor(Math.random() * 30) + 70,
          themes: topics.map(t => t.toLowerCase()),
          commonWords: [name, ...topics.slice(0, 3)],
          tone: mood
        },
        chatHistory: [{
          role: 'user',
          message: `I want to create a story about ${name}`
        }, {
          role: 'bot',
          message: `That's a great idea! Let's make it ${mood} with ${formatTopics(topics)}.`
        }]
      };
      existingStories.push(newStory);
      localStorage.setItem('storyBookIds', JSON.stringify(existingStories));
      return data.book_id;
    } catch (error) {
      console.error('Failed to save story:', error);
      return null;
    }
  };
  // Initialize prompts when showRawPrompt changes
  useEffect(() => {
    if (showRawPrompt) {
      if (editedPrompt === null) {
        setEditedPrompt(generatePrompt());
      }
      if (editedImagePrompt === null && artStyle) {
        setEditedImagePrompt(transformArtStyle(artStyle));
      }
    }
  }, [showRawPrompt]);
  const handleCreate = async () => {
    const bookId = await saveStory();
    onCreate();
    if (bookId) {
      navigate(`/story?book_id=${bookId}`);
    } else {
      console.error('No book_id found from API');
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1173">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 flex flex-col max-h-[90vh]" data-id="element-1174">
        <div className="flex items-center justify-between mb-6 flex-shrink-0" data-id="element-1175">
          <h2 className="text-xl font-bold text-[#6B4B3E] cursor-default" onDoubleClick={() => setShowRawPrompt(!showRawPrompt)} data-id="element-1176">
            Confirm Your Story
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#FFF0E6] rounded-full transition-colors" data-id="element-1177">
            <XIcon size={20} className="text-[#6B4B3E]" data-id="element-1178" />
          </button>
        </div>
        <div className="overflow-y-auto flex-grow" data-id="element-1179">
          <div className="space-y-6 text-[#6B4B3E]" data-id="element-1180">
            <p className="text-lg leading-relaxed" data-id="element-1181">
              Make a story about{' '}
              <span className="font-bold" data-id="element-1182">{name || 'a character'}</span> with{' '}
              <span className="font-bold" data-id="element-1183">{formatTopics(topics)}</span> that
              takes place in <span className="font-bold" data-id="element-1184">{mood}</span>?
            </p>
            {storyText && <div data-id="element-1185">
                <p className="font-medium mb-2" data-id="element-1186">What happens in the story:</p>
                <p className="text-[#6B4B3E]/75 bg-[#FFF0E6] p-4 rounded-xl" data-id="element-1187">
                  {storyText}
                </p>
              </div>}
            {artStyle && <div data-id="element-1188">
                <p className="font-medium mb-2" data-id="element-1189">The art style is:</p>
                <p className="text-[#6B4B3E]/75 bg-[#FFF0E6] p-4 rounded-xl" data-id="element-1190">
                  {artStyle}
                </p>
              </div>}
          </div>
          {showRawPrompt && <>
              <div className="mt-6 mb-4 border-t border-[#E5C1A9] pt-4" data-id="element-1191">
                <div className="flex items-center justify-between mb-2" data-id="element-1192">
                  <h3 className="text-sm font-bold text-[#6B4B3E]" data-id="element-1193">
                    Raw Text Prompt (Click to Edit)
                  </h3>
                  <ChevronDownIcon size={16} className="text-[#6B4B3E]" data-id="element-1194" />
                </div>
                <div className="bg-[#FFF0E6] p-4 rounded-xl" data-id="element-1195">
                  <textarea value={editedPrompt || ''} onChange={e => setEditedPrompt(e.target.value)} className="w-full h-40 text-sm font-mono text-[#6B4B3E]/75 bg-transparent resize-none focus:outline-none" placeholder="Edit your text prompt here..." data-id="element-1196" />
                </div>
              </div>
              {artStyle && <div className="mb-6" data-id="element-1197">
                  <div className="flex items-center justify-between mb-2" data-id="element-1198">
                    <h3 className="text-sm font-bold text-[#6B4B3E]" data-id="element-1199">
                      Raw Image Prompt (Click to Edit)
                    </h3>
                    <ChevronDownIcon size={16} className="text-[#6B4B3E]" data-id="element-1200" />
                  </div>
                  <div className="bg-[#FFF0E6] p-4 rounded-xl" data-id="element-1201">
                    <textarea value={editedImagePrompt || transformArtStyle(artStyle)} onChange={e => setEditedImagePrompt(e.target.value)} className="w-full h-40 text-sm font-mono text-[#6B4B3E]/75 bg-transparent resize-none focus:outline-none" placeholder="Edit your image prompt here..." data-id="element-1202" />
                  </div>
                </div>}
            </>}
        </div>
        <div className="flex justify-end space-x-4 mt-8 flex-shrink-0" data-id="element-1203">
          <button onClick={onClose} className="px-6 py-3 rounded-full border-2 border-[#E5C1A9] text-[#6B4B3E] hover:bg-[#FFF0E6] transition-colors" data-id="element-1204">
            Cancel
          </button>
          <button onClick={handleCreate} className="px-6 py-3 bg-[#D4A088] text-white rounded-full hover:bg-[#C28B73] transition-colors" data-id="element-1205">
            Create The Story
          </button>
        </div>
      </div>
    </div>;
};